import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'

import {
  createPreschoolMonthlyTheme,
  fetchPreschoolMonthlyTheme,
  updatePreschoolMonthlyTheme
} from 'api/preschoolEvaluation'
import { showToast } from 'app/helpers'

import { PRESCHOOL_LEVEL_OPTIONS } from '../constants'

const THEME_MAX = 500

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  spacer: { flexGrow: 1 },
  statusInfo: { color: theme.palette.text.secondary },
  published: { color: theme.palette.success.main, fontWeight: 600 },
  draft: { color: theme.palette.text.secondary, fontWeight: 600 },
  card: { padding: theme.spacing(2), maxWidth: 720 },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(1)
  },
  helpText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: theme.spacing(0.5)
  },
  charCount: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    textAlign: 'right'
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2)
  }
}))

const firstResult = (data) => {
  if (!data) return null
  if (Array.isArray(data)) return data[0] || null
  if (Array.isArray(data.results)) return data.results[0] || null
  return data
}

const MonthlyThemes = () => {
  const classes = useStyles()

  const [level, setLevel] = useState('NUR')
  const [monthKey, setMonthKey] = useState(dayjs().format('YYYY-MM'))
  const [theme, setTheme] = useState('')
  const [themeUuid, setThemeUuid] = useState(null)
  const [isPublished, setIsPublished] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const loadTheme = useCallback(
    async (notify = false) => {
      if (!level || !monthKey) return
      setLoading(true)
      try {
        const data = await fetchPreschoolMonthlyTheme({
          queries: { level, month_key: monthKey }
        })
        const doc = firstResult(data)
        setThemeUuid(doc?.uuid || null)
        setTheme(doc?.theme || '')
        setIsPublished(!!doc?.is_published)
        if (notify) showToast('Monthly theme loaded', 'success')
      } catch (e) {
        showToast('Failed to load monthly theme', 'danger')
      } finally {
        setLoading(false)
      }
    },
    [level, monthKey]
  )

  // Auto-load whenever the cohort or month changes.
  useEffect(() => {
    loadTheme(false)
  }, [loadTheme])

  // Upsert the theme with an explicit published state.
  const upsert = async (publishState) => {
    setSaving(true)
    try {
      const payload = {
        uuid: themeUuid,
        level,
        month_key: monthKey,
        theme,
        is_published: publishState
      }
      const res = themeUuid
        ? await updatePreschoolMonthlyTheme(payload)
        : await createPreschoolMonthlyTheme(payload)
      setThemeUuid(res?.uuid || themeUuid)
      setIsPublished(publishState)
      return true
    } catch (e) {
      showToast(e?.message || 'Save failed', 'danger')
      return false
    } finally {
      setSaving(false)
    }
  }

  const onSaveDraft = async () => {
    // Save the text without changing publish status.
    if (await upsert(isPublished)) showToast('Draft saved', 'success')
  }

  const onPublish = async () => {
    if (await upsert(true)) showToast('Monthly theme published', 'success')
  }

  const onUnpublish = async () => {
    if (!themeUuid) {
      showToast('Nothing to unpublish yet', 'danger')
      return
    }
    if (await upsert(false)) showToast('Moved to draft', 'success')
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.topBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            {Object.entries(PRESCHOOL_LEVEL_OPTIONS).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Month"
          type="month"
          size="small"
          variant="outlined"
          value={monthKey}
          onChange={(e) => setMonthKey(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Typography variant="body2" className={classes.statusInfo}>
          Status:{' '}
          <span className={isPublished ? classes.published : classes.draft}>
            {isPublished ? 'Published' : 'Draft'}
          </span>
        </Typography>

        <Box className={classes.spacer} />
        {loading ? <CircularProgress size={22} /> : null}
      </Box>

      <Paper variant="outlined" className={classes.card}>
        <Typography className={classes.sectionTitle}>Monthly Theme</Typography>
        <TextField
          label="Theme"
          placeholder="Write monthly theme..."
          fullWidth
          multiline
          rows={6}
          variant="outlined"
          size="small"
          margin="dense"
          // history.css ships a global `textarea { position: absolute }` rule
          // that leaks app-wide and collapses MUI multiline fields. Pin this
          // field back into normal flow so it renders at full height.
          inputProps={{ maxLength: THEME_MAX, style: { position: 'static' } }}
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
        />
        <Typography className={classes.charCount}>
          {theme.length}/{THEME_MAX}
        </Typography>
        <Typography className={classes.helpText}>
          Teachers/parents see only published monthly themes.
        </Typography>

        <Box className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            disabled={saving}
            onClick={onSaveDraft}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={onPublish}
            style={saving ? undefined : { backgroundColor: '#2d6a4f', color: '#fff' }}
          >
            Publish
          </Button>
          <Button variant="outlined" disabled={saving} onClick={onUnpublish}>
            Unpublish
          </Button>
          <Button variant="outlined" onClick={() => loadTheme(true)}>
            Reload
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}

export default MonthlyThemes
